import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCMS } from '@/hooks/useCMS';

const DEFAULT = {
  label: 'ჩვენ შესახებ',
  heading_line1: '1999 წლიდან',
  heading_line2: 'სრულყოფილების სტანდარტი',
  body: 'Phoenix Georgia — საქართველოში პრემიუმ სამშენებლო მასალების ყველაზე სანდო მომწოდებელი.\n5,000-ზე მეტი პროექტი, 50+ მსოფლიო ბრენდი, ერთი სახელი.',
  link_label: 'მეტის გაგება',
  link_href: '/about',
  year_badge: '1999',
  image: 'https://readdy.ai/api/search-image?query=elegant%20contemporary%20bathroom%20interior%20with%20large%20format%20Laminam%20porcelain%20tiles&width=1600&height=900&seq=hero2&orientation=landscape',
  image_alt: 'Phoenix Georgia შოურუმი',
  stats: [
    { value: '25+',   label: 'წლის გამოცდილება' },
    { value: '5,000+',label: 'დასრულებული პროექტი' },
    { value: '50+',   label: 'პრემიუმ ბრენდი' },
    { value: '100%',  label: 'ხარისხის გარანტია' },
  ],
};

export default function AboutSnippet() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const d = useCMS('about_snippet', DEFAULT);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('animate-fade-up'); observer.unobserve(entry.target); }
      });
    }, { threshold: 0.1 });
    const els = sectionRef.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative overflow-hidden">
      <div className="relative h-[70vh] min-h-[500px]">
        <img src={d.image} alt={d.image_alt} className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-[#1C1C1C]/40"></div>
        <div className="reveal opacity-0 absolute bottom-0 right-0 bg-white p-10 md:p-14 max-w-lg">
          <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] block mb-4">{d.label}</span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1C1C1C] leading-tight mb-5">
            {d.heading_line1}<br />{d.heading_line2}
          </h2>
          <p className="text-[#1C1C1C]/55 text-sm leading-relaxed mb-7">{d.body}</p>
          <Link to={d.link_href} className="inline-flex items-center gap-3 text-[11px] font-bold tracking-[0.2em] uppercase text-[#1C1C1C] border-b border-[#1C1C1C]/30 pb-1 hover:text-[#ec3145] hover:border-[#ec3145] transition-colors duration-300 cursor-pointer">
            {d.link_label}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="reveal opacity-0 absolute top-10 left-10 md:left-14">
          <div className="font-serif text-[clamp(4rem,10vw,9rem)] font-bold text-white/10 leading-none select-none">{d.year_badge}</div>
        </div>
      </div>
      <div className="reveal opacity-0 bg-[#1C1C1C] grid grid-cols-2 md:grid-cols-4">
        {(d.stats || []).map((s: any, i: number) => (
          <div key={i} className="px-8 md:px-12 py-8 border-r border-white/5 last:border-r-0 border-b md:border-b-0 even:border-r-0 md:even:border-r">
            <div className="font-serif text-3xl md:text-4xl font-bold text-white mb-1">{s.value}</div>
            <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-white/30">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
